import * as React from 'react';
import { Internationalization } from '@syncfusion/ej2-base';
import { padStart } from '@fullcalendar/react';
import moment from 'moment';

import {
  ExportChannelArchitectureAPI,
  GetChannelArchitectureAPI,
  GetSeriesAPI,
  GetTitleGroupLookupAPI,
  ImportChannelArchitectureAPI,
} from 'api';

import LinkIcon from 'assets/icons/channel-architecture-linked-slot.svg';

import Button from 'shared/components/button/Button';
import BootstrapSpinner from 'shared/components/bootstrap-spinner/BootstrapSpinner';

import { OpScheduler } from 'op2mise-react-widgets';

import useStore from 'store/AccountStore';
import { useProgramsStore } from './store/programsStore';
import { formatDateWithMoment, addDaysToDate } from 'utils';

import { ActionTypes, ProgramTypes } from './ChannelArchitecture.d';
import { Constants } from './helper/constants';
import findRelatedBlocks from './helper/findRelatedBlocks';
import { getBlockName, reconstructBlockName } from './utils/architecture.utils';

import ConfirmationModal from './modals/ConfirmationModal';
import DuplicateComponent from './modals/DuplicateComponent';
import ModifyBlock from './modals/ModifyBlock';
import { ImportComponent } from './modals/ImportComponent';
import { Settings } from 'op2mise-react-widgets/OpScheduler';

function CAProgrammmes({
  handleGetEventDate,
  channelInfo,
  dispatch,
  endDate,
  handleSaveChannelArchitecture,
  isDirty,
  numberOfWeeks,
  schedulerProps,
  setIsDirty,
  setNumberOfWeeks,
  startDate,
  schedules,
  setAllEvents,
  handleConstructGridData,
}) {
  const { formTypes, weekdays, singleQuery } = Constants;
  const { user } = useStore((state) => state);
  const instance = new Internationalization();
  const blockData = React.useRef({});
  const [openForm, setOpenForm] = React.useState(false);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const { channelId, channelName } = channelInfo;
  const [isLoading, setIsLoading] = React.useState(false);
  const [selectedSlot, setSelectedSlot] = React.useState({});
  const [modalType, setModalType] = React.useState('');
  const [showDeleteBlockModal, setShowDeleteBlockModal] = React.useState({
    show: false,
    data: null,
  });
  const {
    fetchChannelArchitecture,
    fetchTitleGroupList,
    fetchSeriesList,
    programs,
    setProgramsIsLoading,
    setClockTypeBroadcast,
    getSeriesInfo,
    getTitleGroupInfo,
    _resetPrograms,
  } = useProgramsStore((state) => state);

  const { isClockTypeBroadcast, titleGroupList, seriesList } = programs;
  const { WindowsWindowsTimezoneOptions } = Settings;
  const [isLoadingSchedules, setIsLoadingSchedules] = React.useState(false);

  const updateAllItem = (payload, saveChange) => {
    dispatch({
      actionType: ActionTypes.UPDATEALL,
      payload,
      saveChange,
    });
  };

  const getReferenceList = () => {
    // 1. Normalize input with IIFE
    const rawEvents = (() => {
      if (blockData?.current?.EventGetAll) {
        return blockData.current.EventGetAll().map((e) => ({
          id: e.Id,
          week: e.Week,
          dayOfWeek: e.DayOfWeek,
          startTime: e.StartTime,
          blockName: e.BlockName,
          type: e.Type,
          seriesName: e.SeriesName,
          seasonID: e.SeasonID,
          titleGroupName: e.TitleGroupName,
          blockReference: e.BlockReference,
        }));
      }
      if (Object.keys(schedules).length !== 0) {
        return schedules.programmes.map((e) => ({
          id: e.id,
          week: e.week,
          dayOfWeek: e.dayOfWeek,
          startTime: e.startTime,
          blockName: e.blockName,
          type: e.type,
          seriesName: e.seriesName,
          seasonID: e.seasonID,
          titleGroupName: e.titleGroupName,
          blockReference: e.blockReference,
        }));
      }
      return [];
    })();

    // 2. Filter
    const weekOne = rawEvents.filter((e) => e.week < 2);

    // 3. Sorting
    const sorted = weekOne.sort((a, b) => {
      if (a.dayOfWeek > b.dayOfWeek) return 1;
      if (a.dayOfWeek < b.dayOfWeek) return -1;
      if (a.startTime > b.startTime) return 1;
      if (a.startTime < b.startTime) return -1;
      return 0;
    });

    // 4. Build references
    return sorted.map((event) => {
      const linked = rawEvents.some(
        (x) => x.blockReference === event.blockName
      );

      return {
        id: event.id,
        name: `${event.blockName} (${event.type}${linked ? ', Linked' : ''})`,
        blockName: event.blockName,
        linked,
        series: getSeriesInfo(event.seriesName, +event.seasonID),
        titleGroup: getTitleGroupInfo(event.titleGroupName),
      };
    });
  };

  // Getting date range of today and tomorrow
  // NOTE: Use addDaysToDate(x, y) from date.utils
  const tomorrow = moment()
    .add(1, 'days')
    .toDate();

  const period = `${formatDateWithMoment(new Date())} - ${formatDateWithMoment(
    tomorrow
  )}`;

  const getEndDate = () => {
    return numberOfWeeks > 1
      ? addDaysToDate(startDate, numberOfWeeks * 7)
      : endDate;
  };

  const dateHeaderTemplate = React.useCallback(
    (args) => {
      const day = instance.formatDate(args.date, { skeleton: 'E' });
      return (
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <div
            className="e-header-day"
            style={{ flex: 1, display: 'flex', justifyContent: 'center' }}
          >{`${day}`}</div>
        </div>
      );
    },
    [startDate, endDate]
  );

  const eventFields = (args) => ({
    Id: args?.id,
    Subject: args?.type,
    StartTime: handleGetEventDate(args, 'startTime'), // Field required: Always convert to new Date(...)
    EndTime: handleGetEventDate(args, 'endTime'), // Field required: Always convert to new Date(...)
    IsLinked: args?.link,
    IsSequential: args?.sequential,
    BlockReference: args?.blockReference,
    TitleGroupName: args?.titleGroupName,
    SeriesName: args?.seriesName,
    BlockName: args?.blockName,
    Genre: args?.genre,
    Week: args?.week ?? 1,
    DayOfWeek: args?.dayOfWeek,
    MaxCount: args?.maxCount,
    AvailableDuration: args?.availableDuration,
    Layout: args?.layout,
    Type: args?.type,
    SeasonID: args?.seasonID,
    SeriesID: args?.seriesID ? args?.seriesID : args.seriesID,
    TitleGroupID: args?.titleGroupID,
  });

  const IANASzone = (tz) => {
    return WindowsWindowsTimezoneOptions.find((zone) => zone.label === tz)
      .value;
  };

  const eventTemplate = (args) => {
    const {
      State,
      StartTime,
      EndTime,
      Subject,
      IsSequential,
      IsLinked,
      BlockReference,
      TitleGroupName,
      SeriesName,
      Type,
      BlockReferenceType,
    } = args;
    const adjustBlockReference = (blockReference) => {
      const time = blockReference.slice(3, 8);
      const dow = blockReference.slice(0, 3);

      return channelInfo.timezone
        ? `${dow}${moment(time, 'HH:mm')
            .tz(IANASzone(channelInfo.timezone))
            .format('HH:mm')}`
        : blockReference;
    };

    /** Converts the date value and extracts the time */
    const getTimeString = (value) => {
      return instance.formatDate(value, {
        skeleton: 'hm',
        format: State.timeFormat,
      });
    };

    const displayType = () => {
      return Subject === 'TitleGroup' ? ProgramTypes.TITLEGROUP : Subject;
    };

    const getScheduleBlockSubject = () => {
      const subjects = {
        [ProgramTypes.TITLEGROUP]: TitleGroupName,
        TitleGroup: TitleGroupName,
        [ProgramTypes.SERIES]: SeriesName,
      };
      if (Type === ProgramTypes.REPEAT) {
        return TitleGroupName ?? SeriesName ?? ProgramTypes.REPEAT;
      }
      return subjects[Subject] ?? Subject;
    };
    return (
      <>
        <div className="op-event-subject">{getScheduleBlockSubject()}</div>
        <div className="op-event-content">
          <div className="op-event-schedule">{`${getTimeString(
            StartTime
          )} - ${getTimeString(EndTime)}`}</div>
          <span>{displayType()}</span>

          {((IsLinked && BlockReference) || IsSequential) && (
            <div
              style={{
                position: 'absolute',
                bottom: '1.2%',
                marginBottom: '4px',
                display: 'flex',
                flexDirection: 'row',
              }}
            >
              {/* {IsSequential && ( <img className='icon-link-sequential' src={SequentialIcon} alt='sequential-icon' width={16} height={16}/> )} */}
              {IsLinked && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    width: '100%',
                  }}
                >
                  <img
                    className="icon-link-sequential"
                    src={LinkIcon}
                    alt="link-icon"
                    width={16}
                    height={16}
                  />
                  <span
                    style={{
                      color: 'rgba(250, 250, 250, 0.5)',
                      marginLeft: '1px',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      width: '80%',
                    }}
                  >{`${BlockReference} ${displayType()}`}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </>
    );
  };

  const getSelectedWeek = (e) => {
    if (!e.startTime) return 1;
    const msDay = 24 * 60 * 60 * 1000; // milliseconds per day
    const totalDays = Math.floor(
      (new Date(e.startTime) - new Date(startDate)) / msDay
    );
    const week = Math.floor(totalDays / 7) + 1;
    return week;
  };

  React.useEffect(() => {
    // Close the dialog when the component is unmounted
    setIsDialogOpen(false);
    blockData.current = {};

    return () => {
      _resetPrograms();
    };
  }, []);

  const handleDrag = (args) => {
    // AB: Disable dragging for now as this is causing too much issues.
    // TODO: To rework change tracking in scheduler by also updating data source when
    // dragging/updating blocks to make dragging work.

    const day = instance.formatDate(args.data.StartTime, { skeleton: 'E' });

    args.schedulerActions.EventChange({
      availableDuration: args.data?.AvailableDuration || 0,
      blockName: getBlockName(
        weekdays[day],
        moment(args.data.StartTime, 'HH:mm:ss').format('HH:mm:ss')
      ),
      blockReference: args.data?.BlockReference,
      channelID: channelInfo.channelId,
      dayOfWeek: weekdays[day],
      endTime: moment(args.data.EndTime, 'HH:mm:ss').format('HH:mm:ss'),
      startTime: moment(args.data.StartTime, 'HH:mm:ss').format('HH:mm:ss'),
      genre: args.data?.Genre || null,
      layout: args.data?.Layout,
      link: args.data?.Link ?? false,
      maxCount: args.data?.MaxCount || 0,
      seriesID: args.data?.SeriesId || null,
      seriesName: args.data?.SeriesName || null,
      sequential: false,
      titleGroupID: args.data?.TitleGroupID || null,
      titleGroupName: args.data.TitleGroupName || null,
      type: args.data?.Type || 'Movies',
      week: args.data?.Week,
      id: isNaN(args.data?.Id) ? 0 : args.data.Id,
    });
    const all = args.schedulerActions.EventGetAll();
    setAllEvents(all);
    setIsDirty(true);
  };

  const customFooter = React.useCallback(
    ({ events, schedulerActions }) => {
      return (
        <div style={{ marginTop: '15px', float: 'right' }}>
          <Button
            text="Save"
            style={{ marginLeft: '10px' }}
            onClick={() => {
              handleSaveChannelArchitecture(schedulerActions.EventGetAll());
              schedulerActions.ResetEventHistory();
            }}
            disabled={!isDirty}
          />
        </div>
      );
    },
    [isDirty]
  );

  const handleDuplicatedSlot = (args) => {
    if (args.data.Id) {
      setSelectedSlot({
        ...args.data,
        EventCreate: args.schedulerActions.EventCreate,
        EventGetAll: args.schedulerActions.EventGetAll,
        EventConflictValidator: args.schedulerActions.EventConflictValidator,
      });
      setOpenForm(formTypes.paste);
    }
  };

  const handleOnExport = () => {
    ExportChannelArchitectureAPI({
      queryParams: { channelId },
      onSuccess: (res) => {
        const { organisationName } = user;
        let exportDate = new Date();
        const exportedDateString =
          padStart(exportDate.getDate(), 2).toString() +
          padStart(exportDate.getMonth() + 1, 2).toString() +
          exportDate.getFullYear().toString();
        const fileName = `${organisationName}_op2mise_channel_architecture_programme_export_${channelName}_${exportedDateString}.xlsx`;
        const blob = new Blob([res], {
          type:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        saveAs(blob, fileName);
      },
    });
  };

  const handleOnImport = (args) => {
    if (args) {
      blockData.current = {
        ...args.data,
        EventCreate: args.schedulerActions.EventCreate,
        EventGetAll: args.schedulerActions.EventGetAll,
        EventImport: args.schedulerActions.EventImport,
      };
      setOpenForm(formTypes.import);
    }
  };

  // Block cloning logic
  // Applies to blocks on week 1 Monday and week 2 Monday
  // Usually these are blocks between time slots of 00:00:00 - 06:00:00 (or a long duration block that extends beyond 6AM) when in broadcast clock type
  const isBlockCloned = (args) => {
    let selected = args.data;
    let allEvents = args.schedulerActions.EventGetAll();
    let cloneInfo = {
      isCloned: false,
      cloneData: null,
      cloneId: null,
    };

    //Check if selected is a block on week 2 Monday
    if (args.data.Week === 2 && args.data.DayOfWeek === 1) {
      // instantiate cloneInfo with cloneData and isCloned
      cloneInfo.cloneData = allEvents.find(
        (event) =>
          event.Week === 1 &&
          event.DayOfWeek === args.data.DayOfWeek &&
          args.data.BlockName === event.BlockName
      );
      if (cloneInfo.cloneData) {
        cloneInfo.isCloned = true;
        cloneInfo.cloneId = cloneInfo.cloneData.Id;
      }
    }

    // Check if selected is a block on week 1 Monday
    if (args.data.Week === 1 && args.data.DayOfWeek === 1) {
      cloneInfo.cloneData = allEvents.find(
        (event) =>
          event.Week === 2 &&
          event.DayOfWeek === args.data.DayOfWeek &&
          args.data.BlockName === event.BlockName
      );
      if (cloneInfo.cloneData) {
        cloneInfo.isCloned = true;
        cloneInfo.cloneId = cloneInfo.cloneData.Id;
      }
    }

    return { selected, ...cloneInfo };
  };
  const handleOnSlotEditSeries = (args) => {
    setIsDialogOpen(true);
    setModalType(ActionTypes.MULTIPLEUPDATE);
    const { selected, isCloned, cloneId, cloneData } = isBlockCloned(args);
    blockData.current = {
      ...selected,
      EventDelete: args.schedulerActions.EventDelete,
      EventChange: args.schedulerActions.EventChange,
      EventGetAll: args.schedulerActions.EventGetAll,
      EventConflictValidator: args.schedulerActions.EventConflictValidator,
      ...(isCloned && { cloneId, cloneData }),
    };
  };

  const handleOnSlotModified = (args) => {
    setIsDialogOpen(true);
    setModalType(ActionTypes.UPDATE);
    const { selected, isCloned, cloneId, cloneData } = isBlockCloned(args);
    blockData.current = {
      ...selected,
      EventDelete: args.schedulerActions.EventDelete,
      EventChange: args.schedulerActions.EventChange,
      EventGetAll: args.schedulerActions.EventGetAll,
      EventConflictValidator: args.schedulerActions.EventConflictValidator,
      ...(isCloned && { cloneId, cloneData }),
    };
  };
  const handleOnEventCreate = (args) => {
    blockData.current = {
      EndTime: args.data.endTime,
      StartTime: args.data.startTime,
      Type: 'Movies',
      Layout: 'Sequential',
      Week: getSelectedWeek(args),
      EventCreate: args.schedulerActions.EventCreate,
      EventGetAll: args.schedulerActions.EventGetAll,
      EventConflictValidator: args.schedulerActions.EventConflictValidator,
    };
    setModalType(ActionTypes.CREATE);
    setIsDialogOpen(true);
  };

  const lowerCaseNormalizedData = (all) => {
    return all.map((schedule) => {
      return {
        availableDuration: schedule?.AvailableDuration || 0,
        blockName: getBlockName(schedule.DayOfWeek, schedule.StartTime),
        blockReference: schedule?.BlockReference,
        dayOfWeek: schedule.DayOfWeek,
        endTime: moment(schedule.EndTime, 'HH:mm:ss').format('HH:mm:ss'),
        genre: schedule?.Genre || null,
        layout: schedule?.Layout,
        link: schedule?.IsLinked ? schedule.IsLinked : false,
        maxCount: schedule?.MaxCount || 0,
        seasonID: schedule?.SeasonID || null,
        seriesID: schedule?.SeriesId || null,
        seriesName: schedule?.SeriesName || null,
        sequential: false,
        startTime: moment(schedule.StartTime, 'HH:mm:ss').format('HH:mm:ss'),
        titleGroupID: schedule?.TitleGroupID || null,
        titleGroupName: schedule.TitleGroupName || null,
        type: schedule?.Type || 'Movies',
        week: schedule.Week,
        id: schedule.Id,
      };
    });
  };

  const updateBlockReferences = (list, action, allEvents) => {
    let toUpdate = [];
    if (list.length) {
      const toDeleteBlockNames = list.map((o) => o.blockName);
      const foundBlocks = allEvents.filter((block) => {
        if (toDeleteBlockNames.includes(block.blockReference)) {
          return block;
        }
      });

      if (foundBlocks.length) {
        foundBlocks.forEach((block) => {
          if (block.type === 'Repeat') {
            toUpdate.push({
              ...block,
              blockReference: '',
              link: true,
            });
          } else {
            toUpdate.push({
              ...block,
              blockReference: '',
              link: false,
            });
          }
        });
      }
    }
    if (toUpdate.length) {
      action.EventChange(toUpdate);
    }
  };

  const toCamelCase = (obj) => {
    let newObj = [];
    for (const [key, value] of Object.entries(obj)) {
      const firstLetterLowerCased = key.charAt(0).toLowerCase();
      const camelCasedKey = key.replace(key.charAt(0), firstLetterLowerCased);
      newObj[camelCasedKey] = value;
    }
    return newObj;
  };

  const handleDelete = (args, action) => {
    const { data, schedulerActions } = args;

    setIsDirty(true);

    // Helper to ensure consistent time formatting
    const formatTime = (time) => moment(time).format('HH:mm:ss');

    // 1. Prepare the Primary Block (Current Selection)
    // We need two versions: one for reference updating (raw) and one for deletion (normalized)
    const rawPrimaryBlock = {
      ...data,
      blockName: data.BlockName,
    };

    const normalizedPrimaryBlock = {
      ...toCamelCase(data),
      startTime: formatTime(data.StartTime),
      endTime: formatTime(data.EndTime),
    };

    // 2. Fetch Context
    const allEvents = lowerCaseNormalizedData(schedulerActions.EventGetAll());

    // 3. Determine the Scope of Deletion
    let blocksProcessingList = [];

    if (action === ActionTypes.MULTIPLEDELETE) {
      const relatedBlocks = findRelatedBlocks(data, allEvents, {
        includeRepeats: true,
        includeSelected: false,
        requireExactName: false,
        searchEntireSchedule: true,
      });

      const toDelete =
        data.Type === 'Repeat'
          ? relatedBlocks.filter((result) => result.type === 'Repeat') // Remove parents from list
          : relatedBlocks;
      // Combine related blocks with the normalized primary block
      blocksProcessingList = [...toDelete, normalizedPrimaryBlock];

      // For reference updates, we need the raw primary block combined with related blocks
      updateBlockReferences(
        [...toDelete, rawPrimaryBlock],
        schedulerActions,
        allEvents
      );
    } else {
      // Single delete scope
      blocksProcessingList = [normalizedPrimaryBlock];

      updateBlockReferences([rawPrimaryBlock], schedulerActions, allEvents);
    }

    // 4. Handle Cloned Blocks
    // If the block is cloned, the clone must also be deleted
    const { isCloned, cloneData } = isBlockCloned(args);

    if (isCloned) {
      blocksProcessingList.push(
        toCamelCase({
          ...cloneData,
          startTime: formatTime(cloneData.StartTime),
          endTime: formatTime(cloneData.EndTime),
        })
      );
    }

    // 5. Execute Final Actions
    schedulerActions.EventDelete(blocksProcessingList);
    setShowDeleteBlockModal({ show: false, data: null });
  };

  const handleRepeat = (args, actionType) => {
    setIsDialogOpen(true);
    setModalType(actionType);
    blockData.current = {
      ...args.data,
      EventDelete: args.schedulerActions.EventDelete,
      EventChange: args.schedulerActions.EventChange,
      EventCreate: args.schedulerActions.EventCreate,
      EventGetAll: args.schedulerActions.EventGetAll,
      EventConflictValidator: args.schedulerActions.EventConflictValidator,
    };
  };

  const handleOnDuplicateResult = (args) => {
    if (args && args.length) {
      selectedSlot.EventCreate(handleConstructGridData(args, false));
      setAllEvents(selectedSlot.EventGetAll());
    } else {
      if (args?.id) {
        selectedSlot.EventCreate(handleConstructGridData([args], false));
        setAllEvents(selectedSlot.EventGetAll());
      }
    }
    setIsDirty(true);
  };

  React.useEffect(() => {
    if (channelId || isDirty) {
      setIsLoadingSchedules(true);
      fetchChannelArchitecture(
        channelId,
        startDate,
        endDate,
        isClockTypeBroadcast,
        (args) => {
          updateAllItem({
            programmes: args,
          });
          setIsDirty(false);
        }
      );
      fetchSeriesList(channelId);
    }
    fetchTitleGroupList();
  }, [channelId, isClockTypeBroadcast]);

  const blockProps = React.useMemo(
    () => ({
      allEvents: schedules.programmes?.length
        ? {
            programmes: schedules.programmes?.filter(
              (schedule) => schedule.week === 1 || schedule.week === 0
            ),
          }
        : { programmes: [] },
      setIsDialogOpen,
      blockData: blockData.current,
      numberOfWeeks: 1,
      modalType,
      referenceList: getReferenceList(),
      isBroadcast: isClockTypeBroadcast,
      setIsDirty,
      setAllEvents: (args) => setAllEvents(args),
      setModalType,
      updateBlockReferences,
    }),
    [schedules, blockData.current, isClockTypeBroadcast, modalType]
  );

  const duplicateProps = React.useMemo(
    () => ({
      allEvents: schedules,
      startDate: startDate,
      endDate: endDate,
      setNumberOfWeeks: setNumberOfWeeks,
      channelId: channelId,
      selectedSlot: selectedSlot,
      onClose: () => setOpenForm(''),
      onPaste: handleOnDuplicateResult,
      numberOfWeeks: 1, // Used for the Recurrence Pattern Component
      referenceList: getReferenceList(),
      seriesList,
      titleGroupList,
    }),
    [schedules, channelId, selectedSlot, startDate, endDate]
  );

  React.useEffect(() => {
    // Change between Standard and Broadcast clocktype
    setClockTypeBroadcast(user?.clockType === 'Broadcast');
  }, [user]);

  React.useEffect(() => {
    if (numberOfWeeks === 0) setNumberOfWeeks(1);
  }, [numberOfWeeks]);

  React.useEffect(() => {
    if (isLoadingSchedules) {
      setTimeout(() => {
        setIsLoadingSchedules(false);
      }, 500);
    }
  }, [schedules]);

  return (
    <>
      {showDeleteBlockModal.show && (
        <ConfirmationModal
          text="Do you want to delete all linked slots?"
          handleConfirm={() =>
            handleDelete(showDeleteBlockModal.data, ActionTypes.MULTIPLEDELETE)
          }
          handleCloseModal={() =>
            setShowDeleteBlockModal({ show: false, data: null })
          }
          handleReject={() =>
            handleDelete(showDeleteBlockModal.data, ActionTypes.DELETE)
          }
          showModal={showDeleteBlockModal.show}
        />
      )}
      {isDialogOpen && <ModifyBlock {...blockProps} />}
      {openForm === 'IMPORT' && (
        <ImportComponent
          closeModal={() => setOpenForm(false)}
          setIsDirty={setIsDirty}
          scheduleInfo={{ ...channelInfo, period }}
          schedulerData={{
            schedules: schedules.programmes,
            period: { startTime: startDate, endTime: getEndDate() },
            scheduleInfo: {
              ...channelInfo,
              period,
            },
            seriesList,
            titleGroupList,
          }}
          sampleTemplate="programmeImportTemplate"
          handleOnImportApi={ImportChannelArchitectureAPI}
          dataExists="channelArchitectureExist"
          dataModel="channelArchitectureModel"
          importResultColumns={[
            {
              field: 'dayOfWeek',
              headerText: 'Day of Week',
            },
            {
              field: 'startTime',
              headerText: 'Start Time',
            },
            {
              field: 'endTime',
              headerText: 'End Time',
            },
            {
              field: 'type',
              headerText: 'Type',
            },
            {
              field: 'genre',
              headerText: 'Genre',
            },
          ]}
          importResultFileTitle="programme_import_result"
          setSchedules={(data) => {
            if (data.length) {
              let importedData = handleConstructGridData(data, false);
              blockData.current.EventImport(importedData);
              setAllEvents(blockData.current.EventGetAll());
              setIsLoading(false);
              setIsDirty(true);
            }
          }}
        />
      )}
      {openForm === formTypes.paste && (
        <DuplicateComponent {...duplicateProps} />
      )}
      {channelId === 0 && !channelName ? null : isLoading ? (
        <div style={{ height: 'calc(100vh - 120px)' }}>
          <BootstrapSpinner />
        </div>
      ) : (
        <OpScheduler
          {...schedulerProps}
          actionButtonPanel={(events, schedulerActions) =>
            customFooter(events, schedulerActions)
          }
          dateHeaderTemplate={dateHeaderTemplate}
          dataSource={schedules}
          eventFields={eventFields}
          eventLookupList="programmes"
          eventTemplate={eventTemplate}
          heightBuffer={230}
          isLoading={isLoadingSchedules}
          onEventCreate={handleOnEventCreate}
          onCellDoubleClick={handleOnEventCreate}
          // onEventDrag={handleDrag}
          onExport={handleOnExport}
          onImport={handleOnImport}
          onSlotDeleted={(args) =>
            setShowDeleteBlockModal({ show: true, data: args })
          }
          onSlotRepeat={(args) => handleRepeat(args, ActionTypes.REPEAT)}
          onSlotMultipleRepeat={(args) =>
            handleRepeat(args, ActionTypes.MULTIPLEREPEAT)
          }
          onSlotModified={handleOnSlotModified}
          // onSlotResizeStop={handleDrag}
          schedule={{ start: startDate, end: endDate }}
          settings={['SLOT DURATION', 'SLOT INTERVAL', 'CLOCKTYPES']}
          suppressDragAndResize
          suppressOverlappingSchedules={true}
          suppressQuickInfo={true}
          suppressTooltip={true}
          timeoutDelay={80}
        />
      )}
    </>
  );
}

export default CAProgrammmes;
